'use client';

import type { ReactNode } from 'react';
import { StatCallout } from './StatCallout';
import { PinnedPanelsLayout } from './PinnedPanelsLayout';
import { MARKS } from '@contentful/rich-text-types';
import RichTextInline from 'components/shared/RichTextInline/RichTextInline';
import { renderSuperscript } from 'utils/superscriptUtils';
import Media from 'components/shared/Media/Media';
import RichTextHeadline from 'components/shared/RichTextHeadline/RichTextHeadline';
import RichTextCopy from 'components/shared/RichTextCopy/RichTextCopy';
import CTA, { isValidCta } from 'components/shared/CTA/CTA';
import { liveUpdateAttrs } from 'libs/contentful/utils';
import { getRichTextValue } from 'utils/formatters';
import type { CardsVerticalScrollProps, CardsVerticalScrollItemData } from 'components/CardsVerticalScroll/types';

interface PanelCopyProps {
  card: CardsVerticalScrollItemData;
  eyebrow?: string;
  moduleSysId: string;
  isActive: boolean;
}

const STAT_MARKS = { [MARKS.SUPERSCRIPT]: renderSuperscript };
const boldMark = (children: ReactNode) => <strong className="font-medium">{children}</strong>;
const COPY_MARKS = { [MARKS.BOLD]: boldMark, [MARKS.ITALIC]: boldMark, ...STAT_MARKS };

function PanelCopy({ card, eyebrow, moduleSysId, isActive }: PanelCopyProps) {
  const { statHighlight, statDetail, statIcon } = card;
  const headingMotion = isActive
    ? 'translate-y-0 opacity-100 duration-[750ms]'
    : 'translate-y-[30px] opacity-0 duration-[400ms]';
  const bodyMotion = isActive
    ? 'translate-y-0 opacity-100 duration-[750ms] delay-[100ms]'
    : 'translate-y-[30px] opacity-0 duration-[400ms]';
  return (
    <div className="text-reserve">
      {eyebrow && (
        <div className="font-display text-base font-medium leading-[1.1]" {...liveUpdateAttrs(moduleSysId, 'eyebrow')}>
          {eyebrow}
        </div>
      )}
      <div {...liveUpdateAttrs(card.sys.id, 'headline')}>
        <RichTextHeadline
          headline={card.headline}
          level="h2"
          className={`${eyebrow ? 'mt-7' : ''} font-sans text-[clamp(2.5rem,3.62vw,3.25rem)] font-light leading-none tracking-[-0.005em] transition-[opacity,translate] ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:duration-0 ${headingMotion}`}
        />
      </div>
      <div
        className={`mt-9 flex flex-col font-sans text-sm font-light leading-[1.34] transition-[opacity,translate] ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:duration-0 ${statHighlight ? 'gap-5' : 'gap-3'} ${bodyMotion}`}
        {...liveUpdateAttrs(card.sys.id, 'copy')}>
        {card.copy.json.content.map((block, index) => (
          <div key={index}>
            <RichTextCopy
              copy={{ ...card.copy, json: { ...card.copy.json, content: [block] } }}
              ySpacing="none"
              additionalOptions={{
                renderMark: COPY_MARKS,
              }}
            />
          </div>
        ))}
      </div>
      {statHighlight && (
        <StatCallout
          icon={statIcon ? <Media media={statIcon} roundedImage={false} sizes="31px" /> : undefined}
          highlight={
            <span {...liveUpdateAttrs(card.sys.id, 'statHighlight')}>
              <RichTextInline inline={statHighlight} additionalOptions={{ renderMark: STAT_MARKS }} />
            </span>
          }
          detail={
            statDetail ? (
              <span {...liveUpdateAttrs(card.sys.id, 'statDetail')}>
                <RichTextInline inline={statDetail} additionalOptions={{ renderMark: STAT_MARKS }} />
              </span>
            ) : undefined
          }
        />
      )}
      {isValidCta(card.cta) && (
        <div className="mt-6" {...liveUpdateAttrs(card.sys.id, 'cta')}>
          <CTA cta={card.cta} />
        </div>
      )}
    </div>
  );
}

export function PinnedPanelsVariant({ sys, sectionName, eyebrow, cards }: CardsVerticalScrollProps) {
  return (
    <PinnedPanelsLayout
      sectionName={sectionName}
      label="Story panels"
      panels={(cards?.items ?? []).map((card, index) => ({
        id: card.sys.id,
        sectionName: card.sectionName,
        label: `Show ${getRichTextValue(card.headline) || `panel ${index + 1}`}`,
        image: desktop => (
          <Media
            media={card.desktopMedia}
            mobileMedia={desktop ? undefined : card.mobileMedia}
            className={desktop ? 'h-full w-full object-cover' : 'aspect-[473/535] w-full rounded-[10px] object-cover'}
            sizes={desktop ? '50vw' : '100vw'}
          />
        ),
        content: active => <PanelCopy card={card} eyebrow={eyebrow} moduleSysId={sys.id} isActive={active} />,
      }))}
    />
  );
}
